import routeNames from "constants/route-names";

// The page a user was trying to open before the login challenge travels in the login URL rather
// than in router state: it survives a reload of the login screen, it can be set from outside the
// router (the Apollo bounce when the session cannot be refreshed), and it can be carried into a
// magic link so signing in from the email lands on the same page.
export const POST_LOGIN_PARAM = "next";

// Coming back to one of these after signing in would either bounce the user to the login screen
// again or drop them on a screen that no longer applies, so they are never remembered.
const AUTH_PATHS = [
	routeNames.HOME,
	routeNames.LOGIN,
	routeNames.SIGNUP,
	routeNames.FORGOT_PASSWORD,
	routeNames.EMAIL_VERIFICATION_PENDING,
	routeNames.VERIFY_CODE,
	routeNames.NEW_PASSWORD
];

// Only paths inside the app are accepted. An absolute URL ("https://…") or a protocol-relative
// one ("//host/path") would turn the login screen into an open redirect.
const isInternalPath = (path: string): boolean => path.startsWith("/") && !path.startsWith("//");

/** Login URL that brings the user back to `target` once they sign in. */
export const buildLoginPath = (target: { pathname: string; search: string; hash: string }): string => {
	if (AUTH_PATHS.includes(target.pathname)) return routeNames.LOGIN;

	const targetPath = `${target.pathname}${target.search}${target.hash}`;
	if (!isInternalPath(targetPath)) return routeNames.LOGIN;

	return `${routeNames.LOGIN}?${POST_LOGIN_PARAM}=${encodeURIComponent(targetPath)}`;
};

/** The page remembered on a login URL, if it carries a usable one. */
export const getRememberedPath = (search: string): string | undefined => {
	const remembered = new URLSearchParams(search).get(POST_LOGIN_PARAM);
	return remembered && isInternalPath(remembered) ? remembered : undefined;
};

/** Where to land after a successful sign-in: the remembered page, or the dashboard. */
export const getPostLoginPath = (search: string): string => getRememberedPath(search) ?? routeNames.DASHBOARD;
